import { useEffect, useState } from 'react';
import { Pencil, Navigation2, Clock } from 'lucide-react';
import { apiGetFleets } from '../../../api/services/fleetApi';
import type { FleetRecord } from '../../../types';
import {
  fetchAdminBookingVehicles,
  type AdminBookingVehicle,
} from '../../../api/bookings/bookingApi';
import type { TripDetailsData } from './Step1TripDetails';
import { calculateTransferPrice } from '../../../../constants/transferBooking';
import { calculateHourlyPrice } from '../../../../constants/hourlyBooking';
import { EditBookingVehiclePriceModal } from '../components/EditBookingVehiclePriceModal';

export type VehiclePricingData = {
  selectedVehicles: Record<string, number>;
  vehiclePrice?: number;
  vehiclePriceOverrides: Record<string, number>;
};

type Step3Props = {
  data: VehiclePricingData;
  tripData?: TripDetailsData;
  updateData: (newData: Partial<VehiclePricingData>) => void;
};

type EditingVehicle = {
  id: string;
  name: string;
  price: number;
};

export const Step3VehiclePricing = ({ data, tripData, updateData }: Step3Props) => {
  const [vehicles, setVehicles] = useState<FleetRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [editingVehicle, setEditingVehicle] =
  useState<EditingVehicle | null>(null);

  const handleSavePrice = (price: number) => {
    if (!editingVehicle) return;

    updateData({
      selectedVehicles: {
        [editingVehicle.id]: 1,
      },
      vehiclePrice: price,
      vehiclePriceOverrides: {
        ...data.vehiclePriceOverrides,
        [editingVehicle.id]: price,
      },
    });

    setEditingVehicle(null);
  };

  useEffect(() => {
    let active = true;

    apiGetFleets()
      .then((res) => {
        if (!active) return;
        if (res?.data && res.data.length > 0) {
          const activeFleets = res.data.filter((f) => f.active !== false);
          setVehicles(activeFleets);
          setIsLoading(false);
        } else {
          fetchAdminBookingVehicles()
            .then((fallbackList) => {
              if (!active) return;
              const mapped: FleetRecord[] = fallbackList.map((v) => ({
                id: v.id,
                name: v.name,
                category: v.category,
                categoryId: null,
                brandId: null,
                plate: '',
                image: v.image,
                passengers: v.passengers,
                luggage: v.luggage,
                description: '',
                basePrice: v.price,
                pricePerKm: 0,
                minimumBookingHours: v.minimumBookingHours ?? 1,
                active: true,
              }));
              setVehicles(mapped);
              setIsLoading(false);
            })
            .catch(() => {
              if (active) setIsLoading(false);
            });
        }
      })
      .catch(() => {
        fetchAdminBookingVehicles()
          .then((fallbackList) => {
            if (!active) return;
            const mapped: FleetRecord[] = fallbackList.map((v) => ({
              id: v.id,
              name: v.name,
              category: v.category,
              categoryId: null,
              brandId: null,
              plate: '',
              image: v.image,
              passengers: v.passengers,
              luggage: v.luggage,
              description: '',
              basePrice: v.price,
              pricePerKm: 0,
              minimumBookingHours: v.minimumBookingHours ?? 1,
              active: true,
            }));
            setVehicles(mapped);
            setIsLoading(false);
          })
          .catch(() => {
            if (active) setIsLoading(false);
          });
      });

    return () => {
      active = false;
    };
  }, []);

  const getVehiclePrice = (vehicle: FleetRecord): number => {
    const isTransfers =
      !tripData ||
      tripData.serviceType === 'Transfers' ||
      tripData.serviceType === 'Transfer';
    const isHourly =
      tripData?.serviceType === 'Hourly drivers' ||
      tripData?.serviceType === 'hourly';
    const isTwoWay = tripData?.journeyType === 'round-trip';

    const basePrice = Number(vehicle.basePrice ?? 0);
    const pricePerKm = Number(vehicle.pricePerKm ?? 0);

    if (isHourly) {
      const dist =
        tripData?.distanceKm ||
        (tripData?.durationHours ? tripData.durationHours * 40 : 80);
      return calculateHourlyPrice(basePrice, pricePerKm, dist);
    }

    if (isTransfers) {
      const dist = tripData?.distanceKm || 0;
      const oneWay = calculateTransferPrice(basePrice, pricePerKm, dist);
      return isTwoWay ? oneWay * 2 : oneWay;
    }

    return basePrice || 0;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16 w-full max-w-[1000px] mx-auto">
        <span className="font-host text-[14px] text-gray-500">Loading vehicles...</span>
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className="flex justify-center items-center py-16 w-full max-w-[1000px] mx-auto">
        <span className="font-host text-[14px] text-gray-500">No vehicles available.</span>
      </div>
    );
  }

  const isHourly = tripData?.serviceType === 'Hourly drivers';

  return (
    <div className="flex flex-col gap-6 w-full max-w-[1000px] mx-auto px-[32px]">
      
      {/* Route & Calculation Summary Banner */}
      {tripData && (
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-[14px] bg-[#F8FAFC] border border-[#E2E8F0]">
          <div className="flex items-center gap-3">
            <div className="size-9 rounded-full bg-[#EBF3FF] flex items-center justify-center text-[#3B5998] shrink-0">
              {isHourly ? <Clock size={18} /> : <Navigation2 size={18} className="rotate-45" />}
            </div>
            <div>
              <p className="font-host text-[14px] font-bold text-[#1A1A1A]">
                {isHourly ? (
                  <>Hourly Driver ({tripData.durationHours || 2} Hours • {tripData.distanceKm || 80} km included)</>
                ) : (
                  <>
                    {tripData.pickupLocation || 'Pickup'} → {tripData.dropoffLocation || 'Drop-off'}
                  </>
                )}
              </p>
              <p className="font-host text-[12px] text-[#64748B]">
                {isHourly ? (
                  `From: ${tripData.pickupLocation || 'Salzburg'}`
                ) : (
                  <>
                    Distance: <strong className="text-[#1E293B]">{tripData.distanceKm || 0} km</strong>
                    {tripData.journeyType === 'round-trip' ? ' • Round Trip (2x)' : ' • One Way'}
                  </>
                )}
              </p>
            </div>
          </div>

          <span className="font-host text-[11px] font-medium text-[#1E3A8A] bg-[#DBEAFE] px-3 py-1 rounded-full">
            {tripData.serviceType}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {vehicles.map((vehicle) => {
          const vehicleCount = data.selectedVehicles[vehicle.id] || 0;
          const isSelected = vehicleCount > 0;
          const calculatedPrice = getVehiclePrice(vehicle);
          const displayedPrice = Math.max(
            0,
            Math.round(
              data.vehiclePriceOverrides?.[vehicle.id] ?? calculatedPrice
            )
          );

          return (
            <div
              key={vehicle.id}
              className={`flex flex-col w-full p-4 rounded-[16px] border transition-all cursor-pointer ${
                isSelected 
                  ? 'border-[#B8D877] bg-white shadow-[0_4px_12px_rgba(184,216,119,0.2)]' 
                  : 'border-[#E0E0E0] bg-white hover:border-[#BDBDBD]'
              }`}
              onClick={() => {
                if (!isSelected) {
                  updateData({
                    selectedVehicles: {
                      [vehicle.id]: 1,
                    },
                    vehiclePrice: displayedPrice,
                    vehiclePriceOverrides: {
                      ...data.vehiclePriceOverrides,
                      [vehicle.id]: displayedPrice,
                    },
                  });
                }
              }}
            >
              {/* Top Section: Vehicle Info */}
              <div className="flex items-center justify-between w-full">
                
                {/* Left Side: Image & Details */}
                <div className="flex items-center gap-6 w-full">
                  {/* Vehicle Image */}
                  <div className="w-[160px] h-[90px] flex items-center justify-center shrink-0">
                    <img 
                      src={vehicle.image} 
                      alt={vehicle.name} 
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>

                  {/* Vehicle Details */}
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-[8px] h-[8px] rounded-full bg-[#D9D9D9]" />
                      <span className="font-host text-[12px] font-medium text-[#757575]">
                        {vehicle.category || 'Vehicle'}
                      </span>
                    </div>
                    
                    <h3 className="font-host text-[18px] font-bold text-[#1A1A1A]">
                      {vehicle.name}
                    </h3>

                    <div className="flex items-center gap-3 mt-1">
                      <div className="flex items-center gap-1">
                        <img src="/assets/images/icon-adult.png" alt="Passengers" className="w-[14px] h-[14px] opacity-60" />
                        <span className="font-host text-[13px] font-medium text-[#757575]">{vehicle.passengers}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <img src="/assets/images/icon-child.png" alt="Luggage" className="w-[14px] h-[14px] opacity-60" />
                        <span className="font-host text-[13px] font-medium text-[#757575]">{vehicle.luggage}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Side: Edit button and booking price */}
                <div className="ml-4 flex shrink-0 items-center gap-3">
                  <button
                    type="button"
                    aria-label={`Edit booking price for ${vehicle.name}`}
                    title="Edit price for this booking"
                    onClick={(event) => {
                      event.stopPropagation();

                      setEditingVehicle({
                        id: vehicle.id,
                        name: vehicle.name,
                        price: displayedPrice,
                      });
                    }}
                    className="flex h-[44px] w-[44px] items-center justify-center rounded-[10px] border border-[#D9D9D9] bg-white text-[#1A1A1A] transition-colors hover:border-[#95BE42] hover:bg-[#F4F9E9] hover:text-[#6F9B1D]"
                  >
                    <Pencil size={18} strokeWidth={2} />
                  </button>

                  <div className="flex h-[72px] w-[110px] flex-col items-center justify-center gap-0.5 rounded-[12px] bg-[#C9E589]">
                    <span className="font-host text-[20px] font-bold text-[#1A1A1A]">
                      €
                      {displayedPrice.toLocaleString('en-GB', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}
                    </span>

                    <span className="flex items-center gap-1 font-host text-[10px] font-medium text-[#1A1A1A]">
                      Reserve Now
                      <svg
                        width="4"
                        height="8"
                        viewBox="0 0 5 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1L4 4L1 7"
                          stroke="#1A1A1A"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>

              {/* Bottom Section: Fleet Count (Only visible when selected) */}
              {/* {isSelected && (
                <div 
                  className="mt-4 flex items-center justify-between w-full h-[54px] bg-[#EEEEEE] rounded-[12px] px-5 cursor-default"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="font-host text-[15px] font-medium text-[#1A1A1A]">
                    Number of Vehicles
                  </span>
                  
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => {
                        const newCount = vehicleCount - 1;
                        const newSelected = { ...data.selectedVehicles };
                        if (newCount <= 0) {
                          delete newSelected[vehicle.id];
                        } else {
                          newSelected[vehicle.id] = newCount;
                        }
                        updateData({ selectedVehicles: newSelected });
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-[#D1D1D1] text-[#1A1A1A] hover:bg-[#C0C0C0] transition-colors"
                    >
                      <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1H11" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    
                    <span className="font-host text-[16px] font-bold text-[#1A1A1A] min-w-[12px] text-center">
                      {vehicleCount}
                    </span>
                    
                    <button 
                      onClick={() => {
                        updateData({
                          selectedVehicles: { ...data.selectedVehicles, [vehicle.id]: vehicleCount + 1 }
                        });
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C9E589] text-[#1A1A1A] hover:bg-[#B8D877] transition-colors"
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 6H11M6 1V11" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )} */}
            </div>
          );
        })}
      </div>
      <EditBookingVehiclePriceModal
        open={editingVehicle !== null}
        vehicleName={editingVehicle?.name ?? ''}
        currentPrice={editingVehicle?.price ?? 0}
        onClose={() => setEditingVehicle(null)}
        onSave={handleSavePrice}
      />
    </div>
  );
};
